import * as React from "react"
import type { ViewKey } from "@digits-graphql/frontend/graphql-bearer"
import { useReadLegalEntityMetadataQuery } from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session"

type ReadLegalEntityMetadataQueryResult = ReturnType<typeof useReadLegalEntityMetadataQuery>

export interface TransactionTimestampsContextValue {
  viewKey: ViewKey
  result: ReturnType<typeof buildTransactionTimestampsResult>
}

export const TransactionTimestampsContext = React.createContext<
  TransactionTimestampsContextValue | undefined
>(undefined)

export function useTransactionTimestamps(skip?: boolean) {
  const providerValue = React.useContext(TransactionTimestampsContext)
  const hasProvider = providerValue !== undefined
  const { viewKey, allowFutureDates, skipQuery } = useMetadataQueryInputs(skip)

  useAssertProviderViewKeyMatches({ providerValue, viewKey })

  // Descendants under TransactionTimestampsProvider still call the generated hook
  // unconditionally for Rules of Hooks stability. Apollo's skip: true sets
  // fetchPolicy: "standby", and standby reobserve returns before cache.watch()
  // registers; that skipped descendant query is the cache-watch reduction.
  const { data, loading, error } = useReadLegalEntityMetadataQuery({
    variables: {
      viewKey,
    },
    skip: skipQuery || hasProvider,
  })

  const skippedResult = React.useMemo(
    () =>
      buildTransactionTimestampsResult({
        data: undefined,
        loading: false,
        error: undefined,
        allowFutureDates,
      }),
    [allowFutureDates]
  )

  const directResult = React.useMemo(
    () =>
      buildTransactionTimestampsResult({
        data: skipQuery ? undefined : data,
        loading: skipQuery ? false : loading,
        error: skipQuery ? undefined : error,
        allowFutureDates,
      }),
    [allowFutureDates, data, error, loading, skipQuery]
  )

  if (skip) return skippedResult
  if (providerValue) return providerValue.result
  return directResult
}

export function useTransactionTimestampsProviderValue(): TransactionTimestampsContextValue {
  const { viewKey, allowFutureDates, skipQuery } = useMetadataQueryInputs()
  const { data, loading, error } = useReadLegalEntityMetadataQuery({
    variables: {
      viewKey,
    },
    skip: skipQuery,
  })

  const result = React.useMemo(
    () =>
      buildTransactionTimestampsResult({
        data: skipQuery ? undefined : data,
        loading: skipQuery ? false : loading,
        error: skipQuery ? undefined : error,
        allowFutureDates,
      }),
    [allowFutureDates, data, error, loading, skipQuery]
  )

  return React.useMemo(
    () => ({
      viewKey,
      result,
    }),
    [result, viewKey]
  )
}

function useMetadataQueryInputs(skip?: boolean) {
  const viewKey = useViewVersion()
  const { hasAccessToAspect } = useSession<FrontendSession>()
  const allowFutureDates = hasAccessToAspect?.(AspectCode.FutureDatedTransactions) ?? false
  const skipQuery = useSkipMetadataQuery(viewKey, skip)

  return {
    viewKey,
    allowFutureDates,
    skipQuery,
  }
}

interface BuildTransactionTimestampsResultInput {
  data: ReadLegalEntityMetadataQueryResult["data"] | undefined
  loading: boolean
  error: ReadLegalEntityMetadataQueryResult["error"] | undefined
  allowFutureDates: boolean
}

function buildTransactionTimestampsResult({
  data,
  loading,
  error,
  allowFutureDates,
}: BuildTransactionTimestampsResultInput) {
  const metadata = data?.readLegalEntityMetadata
  const firstTransactionTimestamp = metadata?.firstTransactionTimestamp || 0
  const latestTransactionTimestamp = metadata?.latestTransactionTimestamp || 0
  const firstDate = dateTimeHelper.utcDayjsFromTimestamp(firstTransactionTimestamp)
  const latestDate = dateTimeHelper.utcDayjsFromTimestamp(latestTransactionTimestamp)

  const maxNavigableTimestamp = getMaxNavigableTimestamp({
    allowFutureDates,
    latestTransactionTimestamp,
    loading,
  })
  const maxNavigableYear = dayjs.utc(maxNavigableTimestamp * 1000).year()

  return {
    loading,
    metadataLoaded: data !== undefined || !!error,
    error,
    firstTransactionTimestamp,
    firstTransactionMonth: firstDate.month(),
    firstTransactionYear: firstDate.year(),
    latestTransactionTimestamp,
    latestTransactionMonth: latestDate.month(),
    latestTransactionYear: latestDate.year(),
    maxNavigableTimestamp,
    maxNavigableYear,
  }
}

interface MaxNavigableTimestampInput {
  allowFutureDates: boolean
  latestTransactionTimestamp: number
  loading: boolean
}

function getMaxNavigableTimestamp({
  allowFutureDates,
  latestTransactionTimestamp,
  loading,
}: MaxNavigableTimestampInput) {
  const todayEnd = dayjs.utc().endOf("day").unix()

  if (allowFutureDates && latestTransactionTimestamp) {
    return Math.max(latestTransactionTimestamp, todayEnd)
  }

  if (allowFutureDates && loading) {
    // While metadata is loading, use a generous upper bound to avoid prematurely
    // capping future-dated URLs before the real boundary is known.
    return dayjs.utc().add(10, "year").endOf("year").unix()
  }

  return todayEnd
}

function useSkipMetadataQuery(viewKey: ViewKey | undefined, skip?: boolean): boolean {
  const { location } = useRouter()
  const session = useSession<FrontendSession>()
  const { isSharingContextActive, currentLegalEntity } = session

  const viewKeyLegalEntitySlug = viewKey?.legalEntityId
    ? session.findLegalEntityById(viewKey.legalEntityId)?.slug
    : undefined

  // Parse leSlug from the pathname instead of useRouteMatch().params.
  // useRouteMatch() only sees params from the nearest <Route> ancestor, and
  // TimeContextURLProvider sits above any Route that matches /@:leSlug in the
  // layered router tree, so params.leSlug is always undefined there. Parsing
  // the pathname directly works consistently at every level.
  const leSlug = leSlugFromPathname(location.pathname)

  return shouldSkipMetadataQuery({
    leSlug,
    viewKeyLegalEntitySlug,
    viewKey,
    hasDashboardAccess: !!currentLegalEntity?.hasDashboardAccess(),
    isSharingContextActive,
    callerSkip: !!skip,
  })
}

const LE_SLUG_PATTERN = /^\/@([^/]+)/

function leSlugFromPathname(pathname: string): string | undefined {
  return LE_SLUG_PATTERN.exec(pathname)?.[1]
}

interface SkipMetadataQueryInput {
  leSlug: string | undefined
  viewKeyLegalEntitySlug: string | undefined
  viewKey: ViewKey | undefined
  hasDashboardAccess: boolean
  isSharingContextActive: boolean
  callerSkip: boolean
}

function shouldSkipMetadataQuery({
  leSlug,
  viewKeyLegalEntitySlug,
  viewKey,
  hasDashboardAccess,
  isSharingContextActive,
  callerSkip,
}: SkipMetadataQueryInput): boolean {
  if (callerSkip || isSharingContextActive || !hasDashboardAccess) return true
  if (!leSlug || leSlug !== viewKeyLegalEntitySlug) return true
  if (!viewKey?.legalEntityId || !viewKey?.viewVersion) return true
  return false
}

const VIEW_KEY_FIELDS = ["legalEntityId", "mutationVersion", "viewType", "viewVersion"] as const

interface AssertProviderViewKeyMatchesInput {
  providerValue: TransactionTimestampsContextValue | undefined
  viewKey: ViewKey
}

function useAssertProviderViewKeyMatches({
  providerValue,
  viewKey,
}: AssertProviderViewKeyMatchesInput) {
  if (process.env.NODE_ENV === "production" || !providerValue) return

  const providerViewKey = providerValue.viewKey
  if (areViewKeysEqual(providerViewKey, viewKey)) return

  throw new Error(
    [
      "TransactionTimestampsProvider viewKey mismatch.",
      "A nested ViewVersionContext or ViewTypeContext may have been introduced below the provider.",
    ].join(" ")
  )
}

function areViewKeysEqual(a: ViewKey | undefined, b: ViewKey | undefined) {
  if (!a || !b) return a === b
  return VIEW_KEY_FIELDS.every((field) => a[field] === b[field])
}
